/**
 * Feature-scoped UI context for the SetupPDF view.
 *
 * Replaces scattered `useState` calls for state that is read/written
 * more than one component level away from the view:
 *   - `toggledStoriesVisibilityMap` — drilled View → PdfEnhancementPanel →
 *     PdfStylingTab → TableOfContents (3 intermediate hops).
 *   - `enlargeFonts` — drilled View → PdfEnhancementPanel → PdfStylingTab.
 *
 * `selectedStoryId` stays local to the view because every write goes through
 * `handleStoryChange` (which also manages the PDF controller) and its only
 * consumer outside the view is `StoriesListSidebar` — a direct child.
 */

import React, { createContext, useContext, useMemo, useReducer } from 'react';

type VisibilityMap = { [nodeId: string]: boolean };

export type SetupPdfUiState = {
  toggledStoriesVisibilityMap: VisibilityMap;
  enlargeFonts: boolean;
};

export type SetupPdfUiAction =
  | { type: 'SET_VISIBILITY_MAP'; map: VisibilityMap }
  | { type: 'SET_ENLARGE_FONTS'; value: boolean }
  | { type: 'RESET_VISIBILITY_MAP' };

const defaultState: SetupPdfUiState = {
  toggledStoriesVisibilityMap: {},
  enlargeFonts: false
};

function reducer(state: SetupPdfUiState, action: SetupPdfUiAction): SetupPdfUiState {
  switch (action.type) {
    case 'SET_VISIBILITY_MAP':
      return { ...state, toggledStoriesVisibilityMap: action.map };
    case 'SET_ENLARGE_FONTS':
      return { ...state, enlargeFonts: action.value };
    case 'RESET_VISIBILITY_MAP':
      return { ...state, toggledStoriesVisibilityMap: {} };
    default:
      return state;
  }
}

type SetupPdfUiContextValue = {
  state: SetupPdfUiState;
  dispatch: React.Dispatch<SetupPdfUiAction>;
};

// Safe default lets components render in tests without a provider.
const SetupPdfUiContext = createContext<SetupPdfUiContextValue>({
  state: defaultState,
  dispatch: () => undefined
});

type SetupPdfUiProviderProps = {
  children: React.ReactNode;
  /** Override initial state — used in unit tests only. */
  initialState?: Partial<SetupPdfUiState>;
};

export function SetupPdfUiProvider({ children, initialState }: SetupPdfUiProviderProps) {
  const [state, dispatch] = useReducer(reducer, { ...defaultState, ...initialState });
  const value = useMemo(() => ({ state, dispatch }), [state]);
  return <SetupPdfUiContext.Provider value={value}>{children}</SetupPdfUiContext.Provider>;
}

export function useSetupPdfUi() {
  return useContext(SetupPdfUiContext);
}
